import { makeStyles } from '@material-ui/styles'
import React, { useMemo } from 'react'
import { listTenants, listUserTenants } from 'account/components/userManagement/tenants/new-actions'
import {
  tenantsSelector,
  userTenantsSelector,
} from 'account/components/userManagement/tenants/selectors'
import { useAppSelector } from 'app/store'
import ListTableField from 'core/components/validatedForm/ListTableField'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'

const tenantColumns = [
  { id: 'name', label: 'Name' },
  { id: 'description', label: 'Description' },
]

const useStyles = makeStyles<Theme>((theme) => ({
  tableTitle: {
    flex: 1,
    fontWeight: 600,
    color: theme.palette.grey[900],
  },
}))

export interface TenantAccessFieldProps {
  value: string[]
  onChange: (ids: string[]) => void
  loading?: boolean
  imageOwnerId?: string
  isAdmin?: boolean
}

export default function TenantAccessField({
  value,
  onChange,
  loading,
  imageOwnerId,
  isAdmin,
}: TenantAccessFieldProps) {
  const classes = useStyles()
  // PCD-8206: admins may share with any tenant, so they get the full project list.
  // Non-admins (SSU) must only see the tenants they actually belong to, so they get
  // the user-scoped list (keystone getProjectsAuth). Fetch only the relevant source.
  const { loading: loadingAllTenants } = useListAction(listTenants, { preRequisites: !!isAdmin })
  const { loading: loadingUserTenants } = useListAction(listUserTenants, {
    preRequisites: !isAdmin,
  })
  const allTenants = useAppSelector(tenantsSelector)
  const userTenants = useAppSelector(userTenantsSelector)
  const tenants = isAdmin ? allTenants : userTenants
  const loadingTenants = isAdmin ? loadingAllTenants : loadingUserTenants
  const { prefs } = useScopedPreferences()
  const currentProjectId = prefs.currentTenant
  const availableTenants = useMemo(
    () =>
      tenants.filter(
        (tenant) => tenant.id !== imageOwnerId && (isAdmin || tenant.id !== currentProjectId),
      ),
    [tenants, currentProjectId, imageOwnerId, isAdmin],
  )
  const selectedTenants = useMemo(
    () => availableTenants.filter((tenant) => value.includes(tenant.id)),
    [availableTenants, value],
  )

  const handleChange = (selectedObjects: Array<{ id: string }>) => {
    onChange(selectedObjects.map((t) => t.id))
  }

  return (
    <ListTableField
      id="tenant-access"
      data={availableTenants}
      loading={loading || loadingTenants}
      columns={tenantColumns}
      value={selectedTenants as any} // ListTableField value type infers string[] but needs object[] here
      onChange={handleChange}
      uniqueIdentifier="id"
      multiSelection
      searchTargets={['name', 'description']}
      noRowsPerPage
      extraToolbarContent={
        <Text variant="subtitle2" className={classes.tableTitle}>
          Tenant Access ({availableTenants.length})
        </Text>
      }
    />
  )
}
